import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-login-page" className="min-h-screen bg-ink-900 flex items-center justify-center px-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid opacity-30" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-brand/20 blur-[140px]" />

            <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-ink-200 hover:text-white text-sm">
                <ArrowLeft size={16} /> Back to site
            </Link>

            <div className="relative w-full max-w-md">
                <div className="text-center mb-10">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center mb-4">
                        <Lock size={18} className="text-white" />
                    </div>
                    <h1 className="font-display text-3xl text-white tracking-tight">Admin Studio</h1>
                    <p className="text-ink-300 text-sm mt-2 font-mono">akhmadferdian.com — Content Management</p>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="admin-login-form"
                    className="p-8 rounded-2xl border border-white/10 bg-ink-800/70 backdrop-blur space-y-5"
                >
                    <label className="block">
                        <span className="mono-label block mb-2">Email</span>
                        <div className="relative">
                            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-300" />
                            <input
                                type="email"
                                required
                                data-testid="admin-login-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@akhmadferdian.com"
                                className="w-full bg-black/40 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder:text-ink-400 focus:outline-none focus:border-brand/60 focus:ring-4 focus:ring-brand/10"
                            />
                        </div>
                    </label>
                    <label className="block">
                        <span className="mono-label block mb-2">Password</span>
                        <div className="relative">
                            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-300" />
                            <input
                                type="password"
                                required
                                data-testid="admin-login-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full bg-black/40 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder:text-ink-400 focus:outline-none focus:border-brand/60 focus:ring-4 focus:ring-brand/10"
                            />
                        </div>
                    </label>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-brand text-white font-medium hover:bg-brand-hover transition-colors disabled:opacity-60"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" /> Signing in…
                            </>
                        ) : (
                            "Sign in"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
